import { useEffect, useState } from 'react';

export interface Image {
  image: HTMLImageElement | null;
  isError: boolean;
  isLoaded: boolean;
}

const useLoadImage = (
  imgSrc?: string,
  fallbackImgSrc?: string,
  shouldLoad = true,
): Image => {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!shouldLoad || !imgSrc) return;

    let isCancelled = false;
    let hasLoaded = false;
    const image = new window.Image();

    function loadFallback() {
      if (fallbackImgSrc && imgSrc !== fallbackImgSrc) {
        const fallbackImage = new window.Image();

        fallbackImage.onload = () => {
          if (isCancelled) return;

          setLoadError(false);
          setImage(fallbackImage);
        };
        fallbackImage.onerror = () => {
          if (isCancelled) return;

          setLoadError(true);
          setImage(null);
        };
        fallbackImage.src = fallbackImgSrc;
      } else {
        if (isCancelled) return;

        setLoadError(true);
        setImage(null);
      }
    }

    const timeoutId = setTimeout(() => {
      if (hasLoaded || isCancelled) return;
      // Try loading fallback image after timeout
      loadFallback();
    }, 5000); // fallback after 5 seconds

    image.onload = () => {
      if (isCancelled || hasLoaded) return;

      hasLoaded = true;
      clearTimeout(timeoutId);
      setLoadError(false);
      setImage(image);
    };

    image.onerror = () => {
      if (isCancelled || hasLoaded) return;

      hasLoaded = true;
      clearTimeout(timeoutId);
      // Try loading fallback image on error
      loadFallback();
    };

    image.src = imgSrc;

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [imgSrc, shouldLoad, fallbackImgSrc]);

  return { image, isLoaded: !!image, isError: loadError };
};

export default useLoadImage;
